import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faBell,
  faCheckDouble,
} from "@fortawesome/free-solid-svg-icons";
import NotificationProvider from "../../components/notifications/NotificationProvider";
import NotificationList from "../../components/notifications/NotificationList";
import NotificationFilters from "../../components/notifications/NotificationFilters";
import PageHeader from "../../components/common/PageHeader";
import Loading from "../../components/common/Loading";
import {
  useNotifications,
  useUnreadCount,
} from "../../hooks/notifications/useNotifications";
import { useNotificationQueryContext } from "../../context/NotificationQueryContext";
import { useMarkAllNotificationsAsRead } from "../../hooks/notifications/useNotificationMutations";

/**
 * Inner component that uses the notification context
 */
const NotificationsPageContent = () => {
  const { isAuthenticated } = useAuth0();
  const { queryParams, filters } = useNotificationQueryContext();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use React Query for notifications
  const { data: notificationsData, isLoading: notificationsLoading } =
    useNotifications(queryParams);
  const { data: unreadCountData } = useUnreadCount();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  const notifications = notificationsData?.data?.notifications || [];
  const pagination = notificationsData?.data?.pagination || { hasNext: false };
  const unreadCount = unreadCountData?.data?.unreadCount || 0;

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    if (unreadCount > 0) {
      try {
        await markAllAsReadMutation.mutateAsync();
      } catch (error) {
        console.warn("Failed to mark all notifications as read:", error);
      }
    }
  };

  // Clear initial load state
  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  if (!isAuthenticated || (isInitialLoad && notificationsLoading)) {
    return <Loading />;
  }

  // Page header actions
  const headerActions = [
    unreadCount > 0 && (
      <button
        key="mark-all-read"
        onClick={handleMarkAllAsRead}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-deep-red bg-deep-red/10 hover:bg-deep-red/20 rounded-xl transition-colors duration-200"
        disabled={markAllAsReadMutation.isLoading}
      >
        <FontAwesomeIcon icon={faCheckDouble} className="text-sm" />
        <span>Mark all read</span>
      </button>
    ),
    <Link
      key="preferences"
      to="/notifications/preferences"
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-deep-red hover:bg-deep-red/90 rounded-xl transition-colors duration-200"
    >
      <FontAwesomeIcon icon={faCog} className="text-sm" />
      <span>Preferences</span>
    </Link>,
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Notifications"
        subtitle={
          unreadCount > 0
            ? `${unreadCount} unread notification${
                unreadCount !== 1 ? "s" : ""
              }`
            : notifications.length > 0
            ? "All caught up!"
            : "No notifications yet"
        }
        icon={faBell}
        backTo="/home"
        backLabel="Back"
        actions={headerActions}
      />

      {/* Filters - Always show if there are notifications, active filters, or after initial load */}
      {(notifications.length > 0 ||
        filters.type !== null ||
        filters.read !== null ||
        !isInitialLoad) && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100/50 p-6">
          <NotificationFilters />
        </div>
      )}

      {/* Notification List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100/50 overflow-hidden">
        <NotificationList
          showBulkActions={true}
          showFilters={false}
          virtualScrolling={notifications.length > 50}
        />
      </div>

      {/* Pagination Info - More subtle */}
      {pagination.total > 0 && (
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
            <span>
              Showing {notifications.length} of {pagination.total} notifications
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * NotificationsPage Component
 *
 * Full-page view for managing notifications with filtering, bulk actions, and pagination
 */
const NotificationsPage = () => {
  return (
    <NotificationProvider>
      <NotificationsPageContent />
    </NotificationProvider>
  );
};

export default NotificationsPage;
